/**
 * Negotiation History tab — renders the `GET /contracts/negotiations/:id/histories`
 * log. The endpoint returns 403/500 when the caller is not the creator or an
 * assignee; this component shows an info notice in that case rather than an error.
 */
import { Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { History } from 'lucide-react';
import { useContractNegotiationHistories } from '@/api/contract-negotiations';
import { formatDateTime } from '@/lib/format';
import { TabLoading } from '@/pages/customers/detail/tab-states';

interface NegotiationHistoryTabProps {
  negotiationId: number;
}

/**
 * One history entry row — displays employee name, note, optional status change
 * value, and timestamp. Plain token-only layout; no hand-rolled pills.
 */
function HistoryEntry({
  employeeName,
  note,
  value,
  createdAt,
}: {
  employeeName?: string | null;
  note?: string | null;
  value?: number | null;
  createdAt?: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-1)',
        padding: 'var(--vn-space-3)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-subtle)',
        borderLeft: '3px solid var(--vn-border-strong)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)', fontSize: 'var(--vn-font-size-sm)' }}>
          {employeeName ?? '—'}
        </span>
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
          {formatDateTime(createdAt)}
        </span>
      </div>
      {note ? (
        <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)', whiteSpace: 'pre-wrap' }}>
          {note}
        </p>
      ) : null}
      {value != null ? (
        <p style={{ margin: 0, fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          Value: {value}
        </p>
      ) : null}
    </div>
  );
}

export function NegotiationHistoryTab({ negotiationId }: NegotiationHistoryTabProps) {
  const { data, isLoading, isError } = useContractNegotiationHistories(negotiationId);

  if (isLoading) return <TabLoading rows={3} />;

  // 403/500 when caller is not creator/assignee — show info notice, not an error
  if (isError) {
    return (
      <Alert
        tone="info"
        title="History not available"
      >
        History is only visible to the negotiation creator and assignees.
      </Alert>
    );
  }

  const entries = data?.results ?? [];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>History ({entries.length})</CardTitle>
      </CardHeader>
      <CardBody>
        {entries.length === 0 ? (
          <EmptyState
            icon={<History size={24} />}
            title="No history yet"
            description="Changes to this negotiation will appear here."
          />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {entries.map((entry, i) => (
              <HistoryEntry
                key={entry.contract_negotiation_history_id ?? i}
                employeeName={entry.employee_name}
                note={entry.history_note}
                value={entry.history_value}
                createdAt={entry.created_at}
              />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
